import { useState, useRef, Fragment } from "react";
import ReactDOM from "react-dom";
import { Query } from "@syncfusion/ej2-data";
import {
  Modal,
  Button,
  FilterSelectorOperators as Operators
} from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { hideSpinner, showSpinner } from "@syncfusion/ej2-popups";
import { useAuth } from "oidc-react";
import { useQueryClient } from "@tanstack/react-query";

import useModalEvent from "../../hooks/useModalEvent";
import { ColumnSettings } from "../../types/FilterSelectorSettings";
import { FilterSelector } from "../../components/BulkUpdateTasksModal/FilterSelector";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { UpdateEditor } from "../../components/BulkUpdateTasksModal/UpdateEditor";
import { FilterSummary } from "../../components/BulkUpdateTasksModal/FiltersSummary";
import { useBulkUpdateTasks } from "../../data/mutations/taskMutations";
import useEvent from "../../hooks/useEvent";
import { AppDataManager } from "../../utils/dataManager";
import { buildFilter } from "../../utils/ODataFilterBuilder";
import { isNullOrUndefined } from "../../utils/helpers";
import { TaxDeadlineTabInput } from "../../components/BulkUpdateTasksModal/TaxDeadlineTabInput";
import successImage from "../../content/img/bulk-update-success.svg";
import { UpdateSummary } from "../../components/BulkUpdateTasksModal/UpdateSummary";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const BulkUpdateTaskModalFromActions = () => {
  const { t } = useTranslation(["BulkUpdateTasksModal", "Shared"]);

  const { currentBoardId, currentBoard } = useBoardProvider();

  var updateSettings = {
    Assignee: ColumnSettings.Assignee,
    DueDate: ColumnSettings.DueDate,
    StartDate: ColumnSettings.StartDate,
    TaxYear: ColumnSettings.TaxYear,
    FilingStatus: ColumnSettings.FilingStatus,
    AutoRollover: {
      ...ColumnSettings.AutoRollover,
      descriptionText: t("UpdateEditor.AutoRolloverDescription")
    },
    TaxDeadline: {
      ...ColumnSettings.TaxDeadline,
      type: "custom",
      component: TaxDeadlineTabInput
    },
    Name: {
      ...ColumnSettings.Name,
      type: "Text"
    },
    Tags: {
      ...ColumnSettings.Tags,
      field: "Tags",
      propertyType: "array",
      allowCustomValue: true,
      allowObjectBinding: true
    },
    Collaborators: {
      ...ColumnSettings.Collaborators,
      field: "Collaborators",
      propertyType: "array",
      allowObjectBinding: true
    },
    Stage: {
      field: "StageId",
      type: "source",
      displayProperty: "Title",
      headerText: "Stage"
    }
  };

  const modalRef = useRef(null);
  const updateEditorRef = useRef(null);
  const filterSelectorRef = useRef(null);

  const queryClient = useQueryClient();

  const { mutate: bulkUpdateTasks } = useBulkUpdateTasks();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const { Events, toggleEvent } = useEvent();

  const [disabled, setDisabled] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [taskUpdates, setTaskUpdates] = useState({
    value: null,
    appliedValue: {}
  });
  const [filters, setFilters] = useState({ value: null, flatValue: null });

  const { userManager } = useAuth();
  const { UserId } = useDueDatesAuth();

  const dataManager = new AppDataManager(
    "tasks/filtered",
    userManager.signinSilent.bind(userManager)
  );

  /* MODAL ---------------------------- */
  const openModal = () => {
    setIsVisible(true);
  };

  const closeModal = () => {
    setIsVisible(false);
    onStartOver();
  };

  const toggleModal = () => {
    modalRef?.current?.open();
  };

  useRegisterEvent(ModalEvents.BulkUpdateTaskModalFromActions, toggleModal);

  /* Helpers ---------------------------- */
  const cleanTags = (properties) => {
    /* Remove Ids from new Tags */
    const newValue = { ...properties };
    if (properties?.Tags_Add) {
      newValue.Tags_Add = properties.Tags_Add.map((tag) => {
        if (tag.Title === tag.Id) return { Title: tag.Title };
        else return tag;
      });
    }
    /* Don't remove tags that don't exist */
    if (properties?.Tags_Remove) {
      newValue.Tags_Remove = properties.Tags_Remove.filter(
        (tag) => tag.Title !== tag.Id
      );
    }
    return newValue;
  };

  const splitProperties = (properties) => {
    const taskUpdates = {};
    const taxReturnUpdates = {};
    Object.keys(properties).forEach((propKey) => {
      if (propKey.startsWith("TaskTaxReturnInfo.")) {
        const keys = propKey.split(".");
        taxReturnUpdates[keys[1]] = properties[propKey];
      } else {
        taskUpdates[propKey] = properties[propKey];
      }
    });
    return {
      taskUpdates,
      taxReturnUpdates
    };
  };

  const getValidFilters = (filters) => {
    let validFilters = filters?.filter(
      (x) =>
        !isNullOrUndefined(x.value) &&
        x.value?.length !== 0 &&
        !x.value?.some?.((x) => !x)
    );
    if (!validFilters || validFilters.length < 1) {
      return null;
    } else {
      return validFilters;
    }
  };

  const getODataQuery = (filters) => {
    let flattenedValue = getValidFilters(filters);
    if (!flattenedValue) {
      return "";
    }

    /* Manually construct OData filters for now */
    let filterValues = [];
    flattenedValue.forEach((filter) => {
      let value = "";
      if (filter.field === ColumnSettings.TaxDeadlineExtension.field) {
        value = buildFilter(
          filter.field,
          filter.value?.[0] === true ? Operators.NotEqual : Operators.Equal,
          "null"
        ); /* (ExtendedDate eq null) */
      } else {
        value = buildFilter(
          filter.field,
          filter.operator,
          filter.value,
          filter.columnSettings
        );
      }
      filterValues.push(value);
    });
    return encodeURIComponent(filterValues.join(" and "));
  };

  /* Event Handlers ---------------------------- */
  const onSubmit = async () => {
    showSpinner(modalRef.current.element);

    let formattedUpdates = Object.fromEntries(
      Object.entries(taskUpdates.appliedValue).map(([property, value]) =>
        value === "null" ? [property, null] : [property, value]
      )
    );

    formattedUpdates = cleanTags(formattedUpdates);
    formattedUpdates = splitProperties(formattedUpdates);

    const body = {
      UserId: UserId,
      BoardId: currentBoardId,
      FieldsToUpdate: formattedUpdates.taskUpdates,
      TaxFieldsToUpdate: formattedUpdates.taxReturnUpdates
    };

    const params = {
      $filter: getODataQuery(filters.flatValue)
    };

    bulkUpdateTasks(
      {
        data: body,
        params: { ...(params.$filter ? params : {}) }
      },
      {
        onSuccess: () => {
          hideSpinner(modalRef.current.element);
          setStep(3);
          toggleEvent(Events.RefreshTaskGrid);
          queryClient.invalidateQueries(["get-tags"]);
        },
        onError: () => {
          toast.error(t("Toasts.Error"), {
            position: "bottom-left"
          });
        },
        onSettled: () => {
          hideSpinner(modalRef.current.element);
        }
      }
    );
  };

  const onFilter = async () => {
    const filters = filterSelectorRef.current.getValue();
    const flatValue = filterSelectorRef.current.getFlattenedValue();
    const query = new Query();

    query.addParams("boardId", currentBoardId);
    query.addParams("$filter", getODataQuery(flatValue));
    dataManager.executeQuery(query).then((e) => {
      setTasks(e.result);
    });

    setStep(1);
    setFilters({
      value: filters,
      flatValue: flatValue
    });
  };

  const onEditFilters = () => {
    updateEditorRef.current.closeError();

    setTaskUpdates({
      value: updateEditorRef.current.getValue(),
      appliedValue: updateEditorRef.current.getAppliedValue()
    });

    setTasks(null);
    setStep(0);
  };

  const onUpdateSet = () => {
    const props = {
      value: updateEditorRef.current.getValue(),
      appliedValue: updateEditorRef.current.getAppliedValue()
    };
    if (!getValidFilters(filters.flatValue)) {
      updateEditorRef.current.setError(t("Validation.NoFilters"));
    } else if (!tasks || tasks.length === 0) {
      updateEditorRef.current.setError(t("Validation.NoTasks"));
    } else if (!Object.values(props.value).find((x) => x.hasValue)) {
      updateEditorRef.current.setError(t("Validation.NoUpdates"));
    } else {
      updateEditorRef.current.closeError();
      setTaskUpdates(props);
      setStep(2);
    }
  };

  const onEditProperties = () => {
    setStep(1);
  };

  const onConfirmUpdates = () => {
    onSubmit();
  };

  const onChangeFilters = (filters) => {
    const validFilters = getValidFilters(filters);
    if (!validFilters && !disabled) setDisabled(true);
    if (validFilters && disabled) setDisabled(false);
  };

  const onStartOver = () => {
    setDisabled(true);
    setFilters({ value: null, flatValue: null });
    setTaskUpdates({ value: null, appliedValue: {} });
    setTasks([]);
    setStep(0);
  };

  const ModalHeader = ({
    showBackButton = true,
    actionButtonClass,
    continueText,
    onReturn,
    onContinue,
    subtitleText
  }) => {
    return (
      <div className="header-container">
        <div className="fw-700 fs-16 pb-4">{t("FromActions.Title")}</div>
        <div className="d-flex justify-content-between align-items-center">
          <p className="subtitle">{subtitleText}</p>
          <div className="d-inline-flex gap-4">
            {showBackButton && (
              <Button
                className="w-fit btn-tertiary px-3"
                text={
                  <Fragment>
                    <span className="fa-regular fa-arrow-left et-dark-gray me-2" />
                    {t("FromActions.FilterSelector.Back")}
                  </Fragment>
                }
                onClick={onReturn}
              />
            )}
            <Button
              onClick={onContinue}
              disabled={disabled}
              className={"w-fit btn-primary px-3".concat(
                " ",
                actionButtonClass
              )}
              text={continueText}
            />
          </div>
        </div>
      </div>
    );
  };

  const step1 = (
    <div className="first-step-container">
      <FilterSelector
        id={ModalEvents.BulkUpdateTaskModalFromActions.Id + "_filter-selector"}
        board={currentBoard}
        ref={filterSelectorRef}
        onChange={onChangeFilters}
        value={filters.value}
      />
      <ModalHeader
        continueText={t("FromActions.FilterSelector.Continue")}
        onContinue={onFilter}
        showBackButton={false}
        subtitleText={t("FromActions.FilterSelector.Header")}
      />
    </div>
  );

  const step2 = (
    <div className="second-step-container">
      <div
        id="dd_bulk-update-spinner-target"
        className="bulk-update-modal-second-step"
      >
        <FilterSummary
          onReset={onEditFilters}
          filters={filters?.value?.filter((item) => item.value?.length !== 0)}
          tasks={tasks}
        />

        <UpdateEditor
          id={ModalEvents.BulkUpdateTaskModalFromActions.Id + "_properties"}
          ref={updateEditorRef}
          board={currentBoard}
          value={taskUpdates.value}
          defaultUpdates={[
            updateSettings.Assignee,
            updateSettings.Collaborators,
            updateSettings.Tags,
            updateSettings.Stage,
            updateSettings.Name,
            updateSettings.TaxDeadline,
            updateSettings.TaxYear,
            updateSettings.FilingStatus
          ]}
          columnSettings={Object.values(updateSettings)}
        />
      </div>
      <ModalHeader
        continueText={t("FromActions.FilterSelector.Continue")}
        onContinue={onUpdateSet}
        onReturn={onEditFilters}
        subtitleText={t("FromActions.FilterSelector.Header")}
      />
      <div className="mb-3 fs-12">{t("FromActions.Warning")}</div>
    </div>
  );

  const step3 = (
    <div className="second-step-container">
      <UpdateSummary
        tasks={tasks}
        filters={filters.value}
        updates={taskUpdates.value}
      />
      <ModalHeader
        continueText={t("Review.Submit", { count: tasks?.length ?? 0 })}
        onContinue={onConfirmUpdates}
        onReturn={onEditProperties}
        subtitleText={t("Review.Header")}
        actionButtonClass={"bg-et-green"}
      />
      <div className="mb-3 fs-12">{t("FromActions.Warning")}</div>
    </div>
  );

  const step4 = (
    <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-4 pb-5">
      <img src={successImage} width={"205"} />
      <div>
        <div className="fs-16 fw-700 text-center">
          {t("Success.Title", { count: tasks?.length ?? 0 })}
        </div>
        <div className="text-center">{t("Success.Subtitle")}</div>
      </div>
      <div className="d-flex flex-column">
        <Button
          onClick={() => onStartOver()}
          className={"btn-primary px-3"}
          text={t("Success.ActionButton", { count: tasks?.length ?? 0 })}
        />
        <Button
          onClick={() => modalRef.current.close()}
          className={"btn-tertiary px-3"}
          text={t("Success.Close", { count: tasks?.length ?? 0 })}
        />
      </div>
    </div>
  );

  /* Don't load content till it's visible to avoid the huge load from the components fetching */
  /* keep the refs alive */
  const ModalContent = isVisible ? (
    <>
      {/* <section className={(step===0?"":"d-none")+" h-100"}>{step1}</section>
      <section className={(step===1?"":"d-none")+" h-100"}>{step2}</section>
      <section className={(step===2?"":"d-none")+" h-100"}>{step3}</section>
      <section className={(step===3?"":"d-none")+" h-100"}>{step4}</section> */}
      {[step1, step2, step3, step4][step]}
    </>
  ) : (
    <></>
  );

  return ReactDOM.createPortal(
    <Modal
      id={ModalEvents.BulkUpdateTaskModalFromActions.Id}
      isModal
      ref={modalRef}
      visible={isVisible}
      useSpinner
      open={openModal}
      close={closeModal}
      buttons={[{ foo: "bar" }]} //Why do i need buttons for the footer disappear ???????
      minHeight={"20vh"}
      height={"800px"}
      width={"1140px"}
      content={ModalContent}
    />,
    document.body
  );
};
